import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Spin,
} from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  ArrowLeftOutlined,
  ThunderboltOutlined,
  DownloadOutlined,
  CopyOutlined,
  FileExcelOutlined,
  SaveOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  EMPTY_PROMOTION_DATA,
  tinhGiaNhapThat,
  taoCanhBaoCTKM,
  generatePromotionExcel,
  buildTsvClipboard,
  readPromotionFileText,
  parseExcelPromotionFile,
  smartRegexParsePromotion,
  resolveTieredRate,
  findTierInfo,
  fmt,
  vnd,
  pc,
  num,
} from '../data/pharmacyPromotionPricingData.js';
import { pharmacyToolApi } from '../../../api/pharmacy-tool.api.js';
import { savePendingLead, clearPendingLead, resendPendingLead } from '../../../utils/pendingLead.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import '../styles/pharmacy-promotion-tool.css';

const LOCAL_STORAGE_CMP_KEY = 'ttp_gnt_cmp3';

// Lead gửi lỗi được giữ tạm và tự gửi lại khi mở lại công cụ (HT-06 D-02, YC-TOOL-09)
const LOCAL_STORAGE_PENDING_LEAD = 'ttp_promotion_pending_lead';

export function PharmacyPromotionPricingToolPage() {
  const { user, isAuthenticated } = useAuth();

  // Mở công cụ: nếu còn lead gửi lỗi lần trước thì gửi lại (chạy ngầm, không báo người dùng)
  useEffect(() => {
    resendPendingLead(LOCAL_STORAGE_PENDING_LEAD, (p) => pharmacyToolApi.submitPromotionLead(p));
  }, []);

  // Trạng thái chương trình hiện tại (mặc định rỗng, chờ người dùng nạp dữ liệu)
  const [currentCT, setCurrentCT] = useState(EMPTY_PROMOTION_DATA);

  // Đầu vào nạp chương trình
  const [rawText, setRawText] = useState('');
  const [fileList, setFileList] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiState, setAiState] = useState(null); // { type: 'info' | 'err' | 'ok', msg: '' }

  // Modal báo file/nội dung không phải chương trình khuyến mãi hợp lệ
  const [invalidModalVisible, setInvalidModalVisible] = useState(false);
  const [invalidReason, setInvalidReason] = useState('');

  // Mã sản phẩm được chọn để xem cột giá quy đổi trong bảng 5 ngưỡng
  const [selectedSpIndex, setSelectedSpIndex] = useState(0);

  // Doanh số ước tính (dùng khi chương trình có nhiều bậc thưởng theo doanh số tháng/quý)
  const [estMonthlyRevenue, setEstMonthlyRevenue] = useState(null);
  const [estQuarterlyRevenue, setEstQuarterlyRevenue] = useState(null);

  // Danh sách so sánh (lưu localStorage, tối đa 3 chương trình)
  const [savedCompareList, setSavedCompareList] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_CMP_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  // Modal thu Lead tải file
  const [leadModalVisible, setLeadModalVisible] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [hasUnlockedLead, setHasUnlockedLead] = useState(() => {
    return isAuthenticated || !!localStorage.getItem('ttp_promotion_lead_unlocked');
  });

  // Modal tinh chỉnh thủ công
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();

  const fileInputRef = useRef(null);
  const resultSectionRef = useRef(null);

  // Chương trình có chia nhiều bậc thưởng theo doanh số không (để hiện ô nhập doanh số ước tính)
  const hasTieredBonus = (currentCT.ckThangBac?.length || 0) > 0 || (currentCT.ckQuyBac?.length || 0) > 0;

  // Bậc thưởng tháng/quý đang khớp với doanh số ước tính người dùng nhập (để hiển thị gợi ý)
  const matchedThangTier = useMemo(
    () => findTierInfo(currentCT.ckThangBac, estMonthlyRevenue),
    [currentCT.ckThangBac, estMonthlyRevenue]
  );
  const matchedQuyTier = useMemo(
    () => findTierInfo(currentCT.ckQuyBac, estQuarterlyRevenue),
    [currentCT.ckQuyBac, estQuarterlyRevenue]
  );

  // Chương trình sau khi áp đúng bậc thưởng theo doanh số ước tính (nếu có bảng bậc), dùng để tính toán 5 ngưỡng
  const effectiveCT = useMemo(() => {
    if (!hasTieredBonus) return currentCT;
    return {
      ...currentCT,
      ckThang: resolveTieredRate(currentCT.ckThangBac, estMonthlyRevenue, currentCT.ckThang),
      ckQuy: resolveTieredRate(currentCT.ckQuyBac, estQuarterlyRevenue, currentCT.ckQuy),
    };
  }, [currentCT, hasTieredBonus, estMonthlyRevenue, estQuarterlyRevenue]);

  // Kết quả tính toán 5 tầng ngưỡng
  const R = useMemo(() => {
    return tinhGiaNhapThat(effectiveCT);
  }, [effectiveCT]);

  // Cảnh báo rủi ro chuyên sâu
  const alerts = useMemo(() => {
    return taoCanhBaoCTKM(R);
  }, [R]);

  // Lưu danh sách so sánh vào localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_CMP_KEY, JSON.stringify(savedCompareList));
    } catch (e) { }
  }, [savedCompareList]);

  // Xử lý nạp file
  const handleFilesAdded = (files) => {
    const newFiles = Array.from(files).filter(
      (f) => !fileList.some((existing) => existing.name === f.name && existing.size === f.size)
    );
    if (fileList.length + newFiles.length > 8) {
      toast.warning('Bạn chỉ có thể tải lên tối đa 8 file cùng lúc.');
      return;
    }
    setFileList((prev) => [...prev, ...newFiles]);
    setAiState(null);
  };

  const handleRemoveFile = (index) => {
    setFileList((prev) => prev.filter((_, i) => i !== index));
  };

  // Chuyển file ảnh thành Base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  // Bấm nút "Phân tích chương trình"
  const handleAnalyze = async () => {
    const images = fileList.filter((f) => /^image\//.test(f.type) || /\.(png|jpe?g|webp|gif)$/i.test(f.name));
    const excelFiles = fileList.filter((f) => /\.(xlsx|xls|csv)$/i.test(f.name));
    const otherDocFiles = fileList.filter((f) => !images.includes(f) && !excelFiles.includes(f));
    const textInput = rawText.trim();

    if (!images.length && !excelFiles.length && !otherDocFiles.length && !textInput) {
      setAiState({
        type: 'err',
        msg: 'Chưa có dữ liệu để phân tích. Vui lòng tải file ở Cách 1 hoặc dán nội dung ở Cách 2.',
      });
      return;
    }

    setIsAnalyzing(true);
    setAiState({
      type: 'info',
      msg: 'Đang đọc và gửi dữ liệu cho AI bóc tách chương trình… quá trình thường mất 5–20 giây.',
    });

    try {
      let parsedData = null;
      let rejectReason = null;

      // 1. Nếu có file Excel, đọc trước cấu trúc bảng tính để chuẩn bị danh mục đầy đủ các mã hàng
      let fullExcelResult = null;
      if (excelFiles.length > 0) {
        try {
          fullExcelResult = await parseExcelPromotionFile(excelFiles[0]);
        } catch (e) {
          console.warn('Đọc bảng tính trước:', e);
        }
      }

      // 2. Chuyển đổi file văn bản và Excel thành văn bản sạch để gửi cho AI
      const docTexts = await Promise.all(
        [...excelFiles, ...otherDocFiles].map(async (f) => {
          try {
            const txt = await readPromotionFileText(f);
            return `### FILE: ${f.name}\n${txt}`;
          } catch (err) {
            return `### FILE: ${f.name}\n(Không thể đọc nội dung file: ${err.message})`;
          }
        })
      );

      const combinedText = [textInput, ...docTexts].filter(Boolean).join('\n\n');
      const imageBase64List = await Promise.all(images.slice(0, 4).map((f) => fileToBase64(f)));

      // 3. BẮT BUỘC GỌI API BACKEND CHO AI PHÂN TÍCH (kể cả khi upload file Excel)
      try {
        const res = await pharmacyToolApi.analyzePromotion({
          text: combinedText,
          images: imageBase64List,
        });
        const resPayload =
          res?.data?.sanPham !== undefined
            ? res.data
            : res?.sanPham !== undefined
            ? res
            : res?.data || res;

        if (resPayload) {
          if (resPayload.hopLe === false) {
            rejectReason = resPayload.lyDoTuChoi;
          } else if (resPayload.sanPham && resPayload.sanPham.length) {
            parsedData = resPayload;
          }
        }
      } catch (apiErr) {
        console.warn('API backend AI báo lỗi hoặc offline, chuyển sang phương thức dự phòng:', apiErr);
      }

      // 4. Nếu AI đã phân tích thành công VÀ có file Excel gốc với danh mục đầy đủ nhiều mã hơn (ví dụ 312 mã):
      // Kết hợp độ thông minh của AI (nhận diện đúng tên CT, NCC, chính sách chiết khấu, quà tặng)
      // với toàn bộ danh mục sản phẩm từ file Excel!
      if (parsedData && fullExcelResult && fullExcelResult.sanPham?.length > parsedData.sanPham?.length) {
        parsedData = {
          ...parsedData,
          tenCT: parsedData.tenCT || fullExcelResult.tenCT,
          ncc: parsedData.ncc || fullExcelResult.ncc,
          ckDon: parsedData.ckDon ?? fullExcelResult.ckDon,
          ckThang: parsedData.ckThang ?? fullExcelResult.ckThang,
          ckQuy: parsedData.ckQuy ?? fullExcelResult.ckQuy,
          sanPham: fullExcelResult.sanPham,
          khoanThem: [
            ...(parsedData.khoanThem || []),
            ...(fullExcelResult.khoanThem || []),
          ],
        };
      }

      // 5. Dự phòng: Nếu API offline hoặc không trả về kết quả, sử dụng kết quả đọc Excel trực tiếp hoặc Regex
      if (!parsedData && !rejectReason) {
        if (fullExcelResult && fullExcelResult.hopLe && fullExcelResult.sanPham?.length) {
          parsedData = fullExcelResult;
        } else {
          const localResult = smartRegexParsePromotion(combinedText);
          if (localResult.hopLe === false) {
            rejectReason = localResult.lyDoTuChoi;
          } else {
            parsedData = localResult;
          }
        }
      }

      // 6. Nếu nội dung bị AI hoặc bộ parser từ chối (không phải CTKM) -> báo modal
      if (rejectReason) {
        setAiState(null);
        setInvalidReason(rejectReason);
        setInvalidModalVisible(true);
        setCurrentCT(EMPTY_PROMOTION_DATA);
        setEstMonthlyRevenue(null);
        setEstQuarterlyRevenue(null);
        return;
      }

      if (!parsedData || !parsedData.sanPham || !parsedData.sanPham.length) {
        throw new Error('Không tìm thấy danh sách sản phẩm nào trong nội dung nạp vào. Vui lòng kiểm tra lại file hoặc nội dung.');
      }

      // 5. Cập nhật dữ liệu chương trình
      setCurrentCT({
        tenCT: parsedData.tenCT || 'Chương trình vừa nạp',
        ncc: parsedData.ncc || '',
        uuDaiCongBo: parsedData.uuDaiCongBo !== undefined && parsedData.uuDaiCongBo !== null ? num(parsedData.uuDaiCongBo) : null,
        ckDon: num(parsedData.ckDon),
        ckThang: num(parsedData.ckThang),
        ckQuy: num(parsedData.ckQuy),
        ckThangBac: (parsedData.ckThangBac || []).map((t) => ({
          tu: num(t.tu),
          den: t.den === null || t.den === undefined || t.den === '' ? null : num(t.den),
          tyLe: num(t.tyLe),
        })),
        ckQuyBac: (parsedData.ckQuyBac || []).map((t) => ({
          tu: num(t.tu),
          den: t.den === null || t.den === undefined || t.den === '' ? null : num(t.den),
          tyLe: num(t.tyLe),
        })),
        sanPham: (parsedData.sanPham || []).map((s) => ({
          ten: String(s.ten || 'Sản phẩm').slice(0, 120),
          quyCach: String(s.quyCach || '').slice(0, 60),
          giaNiemYet: num(s.giaNiemYet),
          giaChuaVAT: num(s.giaChuaVAT),
          thueVAT: num(s.thueVAT),
          slMua: num(s.slMua) > 0 ? num(s.slMua) : 1,
          slTang: num(s.slTang),
          ckDong: num(s.ckDong),
        })),
        khoanThem: (parsedData.khoanThem || []).map((k) => ({
          ten: String(k.ten || '').slice(0, 120),
          maCTKM: k.maCTKM || '',
          giaTri: num(k.giaTri),
          quyDoi: k.quyDoi === null || k.quyDoi === undefined || k.quyDoi === '' ? null : num(k.quyDoi),
        })),
      });

      setEstMonthlyRevenue(null);
      setEstQuarterlyRevenue(null);
      setSelectedSpIndex(0);
      setAiState({
        type: 'ok',
        msg: `Đã bóc tách thành công ${parsedData.sanPham.length} mã sản phẩm${parsedData.khoanThem?.length ? ` và ${parsedData.khoanThem.length} khoản kèm theo` : ''}. Kết quả chi tiết hiển thị bên dưới.`,
      });

      if (resultSectionRef.current) {
        resultSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      setAiState({
        type: 'err',
        msg: `Chưa thể bóc tách: ${error.message || 'Lỗi không xác định'}. Vui lòng kiểm tra lại file hoặc dán rõ ràng dạng bảng ở Cách 2.`,
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Tải file Excel
  const handleDownloadExcel = () => {
    if (!hasUnlockedLead && (!user || !user.id)) {
      setLeadModalVisible(true);
      return;
    }

    try {
      const { blob, fileName } = generatePromotionExcel(R);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success(`Đã xuất file Excel ${fileName} thành công (${R.sp.length} mã hàng)!`);
    } catch (e) {
      toast.error(`Không thể tạo file Excel: ${e.message}`);
    }
  };

  // Sao chép TSV vào Clipboard
  const handleCopyClipboard = () => {
    if (!hasUnlockedLead && (!user || !user.id)) {
      setLeadModalVisible(true);
      return;
    }

    try {
      const tsv = buildTsvClipboard(R);
      navigator.clipboard.writeText(tsv).then(
        () => {
          toast.success('Đã sao chép dữ liệu chi tiết từng mã! Hãy mở Excel và bấm Ctrl + V để dán.');
        },
        () => {
          toast.warning('Trình duyệt chặn clipboard tự động. Vui lòng bấm Tải file Excel.');
        }
      );
    } catch (e) {
      toast.error('Không thể sao chép vào clipboard.');
    }
  };

  // Gửi Form Lead khi tải file
  const handleLeadSubmit = async (values) => {
    setIsSubmittingLead(true);
    const leadPayload = {
      name: values.name,
      phone: values.phone,
      pharmacyName: values.pharmacyName || '',
      programName: R.tenCT,
      supplierName: R.ncc,
      totalDiscount: R.tongCK,
      invoiceDiscount: R.ckTrenHoaDon,
      productCount: R.sp.length,
    };
    try {
      await pharmacyToolApi.submitPromotionLead(leadPayload);
      clearPendingLead(LOCAL_STORAGE_PENDING_LEAD);
    } catch (err) {
      console.warn('Lỗi lưu lead (tiếp tục tải file cho người dùng):', err);
      // Lỗi mạng / máy chủ: giữ lại để lần sau mở công cụ gửi tiếp. Dữ liệu sai (400) thì bỏ.
      if (err?.statusCode !== 400) savePendingLead(LOCAL_STORAGE_PENDING_LEAD, leadPayload);
    } finally {
      setIsSubmittingLead(false);
      setLeadModalVisible(false);
      setHasUnlockedLead(true);
      localStorage.setItem('ttp_promotion_lead_unlocked', 'true');
      toast.success('Đăng ký nhận báo cáo thành công!');
      // Tiến hành tải file
      handleDownloadExcel();
    }
  };

  // Lưu vào danh sách so sánh (tối đa 3 chương trình)
  const handleSaveToCompare = () => {
    if (!R) return;
    const newItem = {
      id: Date.now(),
      ten: R.tenCT || 'Chương trình',
      ncc: R.ncc || '',
      tongCK: R.tongCK,
      ckTrenHoaDon: R.ckTrenHoaDon,
      ngoaiHoaDon: R.ngoaiHoaDon,
      soMa: R.sp.length,
      thoiGian: new Date().toLocaleDateString('vi-VN'),
    };

    setSavedCompareList((prev) => {
      const list = prev.length >= 3 ? prev.slice(1) : [...prev];
      return [...list, newItem];
    });

    toast.success(`Đã lưu "${newItem.ten}" vào bảng so sánh!`);
  };

  const handleRemoveCompareItem = (index) => {
    setSavedCompareList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearCompare = () => {
    setSavedCompareList([]);
    toast.info('Đã làm trống danh sách so sánh.');
  };

  // Mở modal tinh chỉnh thủ công
  const handleOpenEditModal = () => {
    editForm.setFieldsValue({
      tenCT: currentCT.tenCT,
      ncc: currentCT.ncc,
      uuDaiCongBo: currentCT.uuDaiCongBo,
      ckDon: currentCT.ckDon,
      ckThang: currentCT.ckThang,
      ckQuy: currentCT.ckQuy,
    });
    setEditModalVisible(true);
  };

  const handleSaveQuickEdit = (values) => {
    setCurrentCT((prev) => ({
      ...prev,
      tenCT: values.tenCT || prev.tenCT,
      ncc: values.ncc || '',
      uuDaiCongBo: values.uuDaiCongBo !== undefined && values.uuDaiCongBo !== null ? num(values.uuDaiCongBo) : null,
      ckDon: num(values.ckDon),
      ckThang: num(values.ckThang),
      ckQuy: num(values.ckQuy),
    }));
    setEditModalVisible(false);
    toast.success('Đã cập nhật các thông số chương trình!');
  };

  // Xác định trạng thái đánh giá Verdict
  const verdictStyle = useMemo(() => {
    if (R.chenhCongBo !== null && R.chenhCongBo > 7) {
      return { cls: 'b-bad', tag: 'Cẩn thận', text: 'Nghe to hơn thực tế khá nhiều' };
    }
    if (R.ngoaiHoaDon > 45) {
      return { cls: 'b-warn', tag: 'Cân nhắc', text: 'Phần lớn chiết khấu không nằm trên hóa đơn' };
    }
    if (R.tongCK >= 20) {
      return { cls: 'b-good', tag: 'Đáng lấy', text: 'Chiết khấu thực tế rất tốt' };
    }
    if (R.tongCK >= 8) {
      return { cls: 'b-good', tag: 'Đáng lấy', text: 'Chiết khấu ở mức bình thường của ngành' };
    }
    return { cls: 'b-warn', tag: 'Cân nhắc', text: 'Chiết khấu mỏng so với rủi ro tồn kho' };
  }, [R]);

  // Sản phẩm đang chọn hiển thị cột giá trong bảng 5 ngưỡng
  const selectedSp = R.sp[selectedSpIndex] || R.sp[0] || null;

  // Chưa có chương trình nào được phân tích (kể cả khi bị AI từ chối) -> không hiển thị bảng số liệu
  const hasNoData = R.sp.length === 0;

  // Kiểm tra chương trình có tính thuế VAT hay không
  const coThueVAT = useMemo(() => {
    return (R.sp || []).some((s) => s.thueVAT > 0);
  }, [R.sp]);

  const dsThueVAT = useMemo(() => {
    const s = new Set();
    (R.sp || []).forEach((x) => {
      if (x.thueVAT > 0) s.add(`${x.thueVAT}%`);
    });
    return Array.from(s).join(', ');
  }, [R.sp]);

  // Tìm chương trình "Rẻ thật nhất" trong bảng so sánh
  const bestCompareIdx = useMemo(() => {
    if (savedCompareList.length <= 1) return -1;
    return savedCompareList.reduce((maxIdx, item, idx) => {
      return item.tongCK > savedCompareList[maxIdx].tongCK ? idx : maxIdx;
    }, 0);
  }, [savedCompareList]);

  return (
    <div className="gnt-wrapper">
      {/* Hero Banner Header */}
      <div className="tool-hero-banner" style={{ background: 'linear-gradient(135deg, #004d32 0%, #006842 50%, #0d7a6f 100%)', color: '#fff', padding: '36px 0 32px' }}>
        <div className="gnt-container">
          <div style={{ marginBottom: 14 }}>
            <Link
              to="/tools"
              style={{
                color: '#d1fae5',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                textDecoration: 'none',
                background: 'rgba(255, 255, 255, 0.12)',
                padding: '4px 14px',
                borderRadius: 20,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowLeftOutlined /> Trung Tâm Công Cụ Thực Chiến
            </Link>
          </div>

          <Row gutter={[24, 24]} align="middle" justify="space-between">
            <Col xs={24} md={17}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Tag color="#10b981" style={{ fontWeight: 700, borderRadius: 4, padding: '2px 8px' }}>
                  BÓC TÁCH KHUYẾN MÃI NCC · GIÁ NHẬP THẬT
                </Tag>
                <span style={{ fontSize: 13, color: '#a7f3d0' }}>Bộ Quy Tắc Kế Toán Thực Chiến Thế Thảo</span>
              </div>
              <h1 style={{ fontSize: 'clamp(26px, 4.5vw, 36px)', lineHeight: 1.2, fontWeight: 800, margin: '8px 0 12px', color: '#ffffff' }}>
                Chương trình này thực ra <span style={{ color: '#fde047' }}>giảm bao nhiêu</span>?
              </h1>
              <p style={{ fontSize: 15, lineHeight: 1.6, maxWidth: 780, color: '#e2e8f0', margin: 0 }}>
                Đưa chương trình nhà cung cấp gửi vào đây. Công cụ bóc tách ra 5 tầng ngưỡng chiết khấu và giá nhập đơn vị tương ứng cho từng mã hàng, phân loại quà tặng nào được trừ giá và quà nào chỉ là hình thức, rồi xuất file Excel 17 cột hoàn chỉnh.
              </p>

              <div className="gnt-privacy">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>
                  <strong>Phép tính chạy an toàn trên máy bạn.</strong> Chỉ nội dung chương trình được bóc tách và phân loại theo bảng quy tắc kế toán chuẩn.
                </span>
              </div>
            </Col>

            <Col xs={24} md={7}>
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(12px)',
                  borderRadius: 14,
                  padding: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  color: '#ffffff',
                }}
              >
                <div style={{ fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a7f3d0', fontWeight: 700 }}>
                  CHUẨN HÓA 5 TẦNG NGƯỠNG
                </div>
                <div style={{ marginTop: 10, display: 'grid', gap: 6, fontSize: 13 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleOutlined style={{ color: '#34d399' }} /> 1. Chiết khấu trực tiếp trên hóa đơn
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleOutlined style={{ color: '#34d399' }} /> 2. Cộng dồn hàng tặng cùng loại (SKU)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleOutlined style={{ color: '#34d399' }} /> 3. Đạt thưởng doanh số tháng
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleOutlined style={{ color: '#34d399' }} /> 4. Đạt thưởng doanh số quý
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircleOutlined style={{ color: '#34d399' }} /> 5. Cộng các khoản kèm theo (quà/du lịch)
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div className="gnt-container">
        {/* PANEL NẠP CHƯƠNG TRÌNH */}
        <div className="gnt-panel">
          <div className="gnt-phead">
            <div>
              <span className="step-badge">Bước 1: Nạp Chương Trình</span>
              <h2 style={{ marginTop: 6 }}>Tải file lên, hoặc dán nội dung tin nhắn Zalo / Email</h2>
            </div>
            <div className="note">
              Chỉ cần tải file hoặc dán text — hệ thống tự động bóc tách thành bảng số liệu
            </div>
          </div>

          <div className="gnt-pbody">
            <div className="gnt-twoway">
              {/* Cách 1: Tải file lên */}
              <div className="gnt-way">
                <div className="gnt-waynum">Cách 1: Tải file lên</div>
                <h3>Kéo thả file tờ CTKM, Excel, ảnh Zalo</h3>
                <div
                  className="gnt-drop"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('hot');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('hot');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('hot');
                    if (e.dataTransfer.files) handleFilesAdded(e.dataTransfer.files);
                  }}
                >
                  <UploadOutlined style={{ fontSize: 32, color: '#005537', marginBottom: 6 }} />
                  <p>
                    Kéo thả file vào đây hoặc bấm nút bên dưới. Hỗ trợ ảnh chụp màn hình Zalo, tờ rơi, file Excel (.xlsx, .xls, .csv), Word (.docx) hoặc TXT.
                  </p>
                  <Button
                    type="default"
                    icon={<UploadOutlined />}
                    onClick={() => fileInputRef.current?.click()}
                    style={{ fontWeight: 600, borderColor: '#005537', color: '#005537' }}
                  >
                    Chọn file từ máy
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    hidden
                    accept="image/*,.xlsx,.xls,.csv,.txt,.docx"
                    onChange={(e) => {
                      if (e.target.files) handleFilesAdded(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  {fileList.length > 0 && (
                    <div className="gnt-filelist">
                      {fileList.map((f, i) => (
                        <span key={i} className="gnt-ftag">
                          {f.name}
                          <button type="button" onClick={() => handleRemoveFile(i)} title="Xóa file">
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Cách 2: Dán text */}
              <div className="gnt-way">
                <div className="gnt-waynum">Cách 2: Dán nội dung hoặc ghi chú thêm</div>
                <h3>Dán bảng giá, nội dung hãng gửi</h3>
                <textarea
                  className="gnt-textarea"
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder={`Dán nội dung chương trình vào đây — tin nhắn Zalo của Trình dược viên, email của hãng, hoặc copy các ô từ file Excel.\n\nDùng được cả khi bạn đã tải ảnh ở Cách 1: gõ bổ sung phần nào ảnh bị mờ, hoặc các cam kết hãng nói miệng chưa ghi trong tờ rơi.`}
                />
              </div>
            </div>

            {/* Tip box kế toán */}
            <div className="gnt-tip">
              <strong>💡 Mẹo kế toán khi quy đổi quà tặng & chính sách kèm theo:</strong>
              <br />
              Nếu chương trình có quà (du lịch, vàng, xe máy, voucher, hàng tặng khác loại, hỗ trợ trưng bày) hãy ghi rõ giá trị vào ô Cách 2 (Ví dụ: <em>“Du lịch Đà Nẵng 20 triệu”</em>, <em>“Tặng 1 chỉ vàng”</em>).
              <br />
              Công cụ áp dụng quy tắc thận trọng của kế toán dược: <strong>Du lịch trừ 60%</strong> giá hãng báo · <strong>Voucher bên thứ ba & Xe máy/Hiện kim trừ 70%</strong> · <strong>Vàng trừ 100%</strong> nguyên giá · <strong>Hàng tặng cùng loại trừ đủ</strong> · Riêng quà vật dụng nhỏ (áo mưa, mũ, cốc), POSM, kệ quầy, hỗ trợ marketing/trưng bày thì <strong>không trừ vào giá nhập (0%)</strong>.
            </div>

            {/* Các nút hành động chính */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 22 }}>
              <Button
                type="primary"
                size="large"
                icon={<ThunderboltOutlined />}
                loading={isAnalyzing}
                onClick={handleAnalyze}
                style={{
                  background: '#005537',
                  borderColor: '#005537',
                  fontWeight: 700,
                  height: 44,
                  padding: '0 28px',
                  boxShadow: '0 4px 12px rgba(0, 85, 55, 0.25)',
                }}
              >
                {isAnalyzing ? 'Đang bóc tách chương trình…' : 'Phân Tích Chương Trình'}
              </Button>

              <Button
                size="large"
                icon={<EditOutlined />}
                onClick={handleOpenEditModal}
                style={{ fontWeight: 600, height: 44, padding: '0 20px', color: '#0d7a6f', borderColor: '#0d7a6f' }}
              >
                Tinh Chỉnh Số Liệu Thủ Công
              </Button>
            </div>

            {/* Trạng thái bóc tách */}
            {aiState && (
              <div className={`gnt-aistate ${aiState.type}`}>
                {aiState.type === 'info' && <Spin size="small" />}
                {aiState.type === 'ok' && <CheckCircleOutlined style={{ fontSize: 16 }} />}
                {aiState.type === 'err' && <CloseCircleOutlined style={{ fontSize: 16 }} />}
                <span>{aiState.msg}</span>
              </div>
            )}
          </div>
        </div>

        {/* SECTION KẾT QUẢ */}
        <div ref={resultSectionRef}>
          <div className="gnt-sechead">
            <span className="eyebrow">Kết quả phân tích chi tiết</span>
            <h2>Chương trình này giảm thật bao nhiêu?</h2>
            <p>
              {hasNoData ? (
                <span>
                  <strong>Chưa có chương trình nào được phân tích.</strong> Vui lòng tải file hoặc dán nội dung ở Bước 1 bên trên và bấm <em>"Phân Tích Chương Trình"</em>.
                </span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>
                    <strong>{R.tenCT}</strong>
                    {R.ncc ? ` · ${R.ncc}` : ''} · Danh mục {R.sp.length} mã hàng
                  </span>
                  {coThueVAT ? (
                    <Tag color="blue" style={{ fontWeight: 600, borderRadius: 4, margin: 0 }}>
                      ĐÃ TÍNH THUẾ VAT ({dsThueVAT})
                    </Tag>
                  ) : (
                    <Tag color="default" style={{ fontWeight: 500, borderRadius: 4, margin: 0, color: '#64748b' }}>
                      Giá chưa gồm VAT (0%)
                    </Tag>
                  )}
                </span>
              )}
            </p>
          </div>

          {hasNoData ? (
            <div className="gnt-panel" style={{ textAlign: 'center', padding: '48px 24px', color: '#64748b' }}>
              <FileExcelOutlined style={{ fontSize: 36, color: '#94a3b8', marginBottom: 12 }} />
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                Chưa có dữ liệu phân tích
              </h3>
              <p style={{ margin: '0 auto', maxWidth: 520, fontSize: 14, color: '#64748b' }}>
                Vui lòng tải lên file bảng giá (Excel, Word, Text), ảnh chụp bảng khuyến mãi hoặc dán nội dung ở Bước 1 bên trên rồi bấm <strong>"Phân Tích Chương Trình"</strong> để xem kết quả bóc tách 5 tầng chiết khấu.
              </p>
            </div>
          ) : (
            <>
          {/* VERDICT CARD */}
          <div className="gnt-panel gnt-verdict">
            <span className={`gnt-band ${verdictStyle.cls}`}>
              <i /> {verdictStyle.tag}
            </span>

            <div className="gnt-bignum">
              <div className="num-box">
                <div className="n" style={{ color: '#005537' }}>
                  {pc(R.tongCK)}
                </div>
                <div className="l">Nếu đạt hết mọi ngưỡng</div>
              </div>

              <div className="num-box">
                <div className="n" style={{ color: '#10221f' }}>
                  {pc(R.ckTrenHoaDon)}
                </div>
                <div className="l">Ngay trên hóa đơn thanh toán</div>
              </div>

              {R.uuDaiCongBo !== null && (
                <div className="num-box">
                  <div className="n" style={{ color: '#64748b' }}>
                    {pc(R.uuDaiCongBo)}
                  </div>
                  <div className="l">Mức hãng công bố truyền thông</div>
                </div>
              )}
            </div>

            <p className="exp">
              <strong>{verdictStyle.text}.</strong> Con số bên trái (<strong>{pc(R.tongCK)}</strong>) là mức giảm giá tổng hợp cao nhất khi bạn hoàn thành toàn bộ chỉ tiêu tháng, quý và nhận đủ quà tặng quy đổi. Con số bên phải (<strong>{pc(R.ckTrenHoaDon)}</strong>) là mức chiết khấu nằm ngay trên hóa đơn — phần tiền bạn chắc chắn được giảm trừ ngay khi nhận hàng mà không phụ thuộc vào bất kỳ điều kiện nào sau đó.
              {R.chenhCongBo !== null && (
                <span>
                  {' '}Hãng công bố mức ưu đãi là <strong>{pc(R.uuDaiCongBo)}</strong>,
                  {R.chenhCongBo > 1
                    ? ` thực chất cao hơn thực tế ${fmt(R.chenhCongBo, 1)} điểm phần trăm.`
                    : R.chenhCongBo < -1
                    ? ` thực chất thấp hơn thực tế ${fmt(-R.chenhCongBo, 1)} điểm phần trăm.`
                    : ' cơ bản khớp với tính toán thực tế.'}
                </span>
              )}
            </p>
          </div>

          {/* BẢNG 5 NGƯỠNG (WATERFALL) */}
          <div className="gnt-sechead">
            <span className="eyebrow">Bóc từng tầng ngưỡng</span>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <h2>Chiết khấu và giá nhập tại mỗi ngưỡng</h2>
              {R.sp.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Xem giá quy đổi của:</span>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    value={selectedSpIndex}
                    onChange={setSelectedSpIndex}
                    style={{ minWidth: 260, maxWidth: 420 }}
                    options={R.sp.map((s, idx) => ({
                      value: idx,
                      label: `${s.ten} (${vnd(s.G0)}đ${s.thueVAT > 0 ? ` · VAT ${s.thueVAT}%` : ''})`,
                    }))}
                  />
                </div>
              )}
            </div>
            <p>
              Giá quy về một đơn vị hàng thực nhận. Mỗi dòng là một ngưỡng: đạt tới điều kiện nào thì chiết khấu cộng dồn và giá nhập là bấy nhiêu.
            </p>
          </div>

          {hasTieredBonus && (
            <div className="gnt-panel" style={{ marginBottom: 12, background: '#f8fafc' }}>
              <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0d7a6f', marginBottom: 8 }}>
                Chương trình này có nhiều bậc thưởng theo doanh số — nhập doanh số ước tính để xem đúng mức bạn đạt được
              </div>
              <Row gutter={16}>
                {currentCT.ckThangBac?.length > 0 && (
                  <Col xs={24} md={12}>
                    <div style={{ marginBottom: 4, fontSize: 13, color: '#475569' }}>Doanh số tháng ước tính</div>
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={1000000}
                      placeholder="Nhập doanh số mua trong tháng (đồng)"
                      formatter={(v) => (v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                      parser={(v) => v.replace(/,/g, '')}
                      value={estMonthlyRevenue}
                      onChange={setEstMonthlyRevenue}
                    />
                    <div style={{ marginTop: 4, fontSize: 12.5, color: matchedThangTier ? '#059669' : '#94a3b8' }}>
                      {matchedThangTier
                        ? `Đang áp dụng bậc ${pc(matchedThangTier.tyLe)} (doanh số ${vnd(matchedThangTier.tu)}đ${matchedThangTier.den ? ` – dưới ${vnd(matchedThangTier.den)}đ` : ' trở lên'})`
                        : `Chưa nhập hoặc chưa đạt bậc thấp nhất — đang tạm tính ${pc(currentCT.ckThang)} (bậc thấp nhất)`}
                    </div>
                  </Col>
                )}
                {currentCT.ckQuyBac?.length > 0 && (
                  <Col xs={24} md={12}>
                    <div style={{ marginBottom: 4, fontSize: 13, color: '#475569' }}>Doanh số quý ước tính</div>
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={1000000}
                      placeholder="Nhập doanh số mua trong quý (đồng)"
                      formatter={(v) => (v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                      parser={(v) => v.replace(/,/g, '')}
                      value={estQuarterlyRevenue}
                      onChange={setEstQuarterlyRevenue}
                    />
                    <div style={{ marginTop: 4, fontSize: 12.5, color: matchedQuyTier ? '#059669' : '#94a3b8' }}>
                      {matchedQuyTier
                        ? `Đang áp dụng bậc ${pc(matchedQuyTier.tyLe)} (doanh số ${vnd(matchedQuyTier.tu)}đ${matchedQuyTier.den ? ` – dưới ${vnd(matchedQuyTier.den)}đ` : ' trở lên'})`
                        : `Chưa nhập hoặc chưa đạt bậc thấp nhất — đang tạm tính ${pc(currentCT.ckQuy)} (bậc thấp nhất)`}
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          )}

          <div className="gnt-panel" style={{ marginTop: 12 }}>
            <div className="gnt-tblwrap">
              <table className="gnt-table">
                <thead>
                  <tr>
                    <th>Tầng Ngưỡng</th>
                    <th>Cộng thêm</th>
                    <th>CK Cộng dồn</th>
                    {selectedSp && <th>Giá nhập đơn vị ({selectedSp.ten})</th>}
                  </tr>
                </thead>
                <tbody>
                  {/* Dòng gốc: Giá niêm yết */}
                  <tr className="base">
                    <td>
                      <strong>
                        Giá niêm yết ban đầu{' '}
                        {selectedSp?.thueVAT > 0 && (
                          <span style={{ color: '#0284c7', fontSize: 12, fontWeight: 600 }}>
                            (Đã gồm VAT {selectedSp.thueVAT}%)
                          </span>
                        )}
                      </strong>
                      <span className="sub">
                        {selectedSp?.thueVAT > 0 && selectedSp?.giaChuaVAT ? (
                          <>Nguyên giá chưa thuế: {vnd(selectedSp.giaChuaVAT)}đ · Thuế VAT: {selectedSp.thueVAT}%</>
                        ) : (
                          'Giá công bố trước mọi chiết khấu (chưa thuế)'
                        )}
                      </span>
                    </td>
                    <td>—</td>
                    <td className="run">0,0%</td>
                    {selectedSp && <td>{vnd(selectedSp.G0)}đ</td>}
                  </tr>

                  {/* 5 tầng ngưỡng */}
                  {R.nguong.map((n, i) => {
                    let giaCot = null;
                    if (selectedSp) {
                      const giaArr = [selectedSp.G1, selectedSp.G2, selectedSp.G3, selectedSp.G4, selectedSp.G5];
                      giaCot = giaArr[i];
                    }
                    const isOff = n.ck <= 0.001;

                    return (
                      <tr key={n.id} className={isOff ? 'off' : ''}>
                        <td>
                          <strong>{n.ten}</strong>
                          <span className="sub">{n.giai}</span>
                        </td>
                        <td>{n.ck > 0.001 ? `+${pc(n.ck)}` : '—'}</td>
                        <td className="run">{pc(n.congDon)}</td>
                        {selectedSp && <td>{vnd(giaCot)}đ</td>}
                      </tr>
                    );
                  })}

                  {/* Dòng tổng kết */}
                  <tr className="tot">
                    <td>
                      <strong>CỘNG LẠI NẾU ĐẠT HẾT NGƯỠNG (GIÁ NHẬP CUỐI)</strong>
                      <span className="sub">Đã tính trọn vẹn chiết khấu đơn, hàng tặng, thưởng doanh số và quà quy đổi</span>
                    </td>
                    <td></td>
                    <td className="run">{pc(R.tongCK)}</td>
                    {selectedSp && <td className="run">{vnd(selectedSp.G5)}đ</td>}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* BẢNG CÁC KHOẢN KÈM THEO (KHOAN THEM) */}
          {R.khoan.length > 0 && (
            <>
              <div className="gnt-sechead">
                <span className="eyebrow">Khoản kèm theo & Quà tặng</span>
                <h2>Chi tiết phân loại & Mức được trừ vào giá vốn</h2>
                <p>Áp dụng theo đúng bảng quy tắc phân loại 20 mã CTKM của kế toán Thế Thảo Pharma.</p>
              </div>

              <div className="gnt-panel" style={{ marginTop: 12 }}>
                <div className="gnt-tblwrap">
                  <table className="gnt-table">
                    <thead>
                      <tr>
                        <th>Khoản kèm theo</th>
                        <th>Phân loại kế toán</th>
                        <th>Hãng báo</th>
                        <th>Mức trừ giá</th>
                        <th>Tính vào giá nhập</th>
                      </tr>
                    </thead>
                    <tbody>
                      {R.khoan.map((k, idx) => {
                        const isNoDeduct = k.heSo === 0;
                        return (
                          <tr key={idx} className={isNoDeduct ? 'off' : ''}>
                            <td>
                              <strong>{k.ten}</strong>
                            </td>
                            <td>
                              <span className="gnt-ma-badge">{k.ma || '—'}</span> {k.ruleTen}
                            </td>
                            <td>{vnd(k.giaTri)}đ</td>
                            <td>
                              {isNoDeduct ? (
                                <Tag color="error" style={{ fontWeight: 600 }}>
                                  0% (Không trừ)
                                </Tag>
                              ) : (
                                <Tag color="success" style={{ fontWeight: 600 }}>
                                  Trừ {k.heSo}%
                                </Tag>
                              )}
                            </td>
                            <td className={isNoDeduct ? '' : 'run'}>
                              {isNoDeduct ? '—' : `${vnd(k.tien)}đ`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* CHI TIẾT TỪNG MÃ & TẢI FILE EXCEL */}
          <div className="gnt-sechead">
            <span className="eyebrow">Xuất dữ liệu cho toàn bộ danh mục</span>
            <h2>Xem trước & Tải file Excel chi tiết {coThueVAT ? '19' : '17'} cột</h2>
            <p>
              Chương trình có tổng cộng <strong>{R.sp.length} mã hàng</strong>. Bảng xem trước chỉ hiện 4 mã đầu tiên — file Excel tải về chứa đầy đủ {coThueVAT ? '19' : '17'} cột chiết khấu{coThueVAT ? ', thuế VAT' : ''} và giá nhập tại từng tầng ngưỡng cho toàn bộ các mã.
            </p>
          </div>

          <div className="gnt-panel">
            <div className="gnt-dlbox">
              {/* Xem trước mini table */}
              <div>
                <table className="gnt-mini">
                  <thead>
                    <tr>
                      <th>Tên sản phẩm</th>
                      <th>Giá niêm yết</th>
                      {coThueVAT && <th>Thuế VAT</th>}
                      <th>Giá trên HĐ</th>
                      <th>Giá nhập cuối</th>
                      <th>Tổng CK</th>
                    </tr>
                  </thead>
                  <tbody>
                    {R.sp.slice(0, 4).map((s, idx) => (
                      <tr key={idx}>
                        <td>
                          <strong>{s.ten}</strong>
                          {s.quyCach && <span style={{ display: 'block', fontSize: 12, color: '#64748b' }}>{s.quyCach}</span>}
                        </td>
                        <td>
                          {vnd(s.G0)}đ
                          {s.thueVAT > 0 && s.giaChuaVAT > 0 && (
                            <span style={{ display: 'block', fontSize: 11, color: '#64748b' }}>
                              chưa VAT: {vnd(s.giaChuaVAT)}đ
                            </span>
                          )}
                        </td>
                        {coThueVAT && (
                          <td>
                            <Tag color={s.thueVAT > 0 ? 'blue' : 'default'} style={{ margin: 0, fontWeight: 600 }}>
                              {s.thueVAT > 0 ? `${s.thueVAT}%` : '0%'}
                            </Tag>
                          </td>
                        )}
                        <td>{vnd(s.G1)}đ</td>
                        <td className="hi">{vnd(s.G5)}đ</td>
                        <td style={{ fontWeight: 700, color: '#005537' }}>{pc(s.ck5)}</td>
                      </tr>
                    ))}
                    {R.sp.length > 4 && (
                      <tr className="more">
                        <td colSpan={coThueVAT ? 6 : 5}>… và còn {R.sp.length - 4} mã sản phẩm khác trong file Excel hoàn chỉnh</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Nút tải file và copy */}
              <div className="gnt-dlact">
                <Button
                  type="primary"
                  size="large"
                  icon={<FileExcelOutlined />}
                  onClick={handleDownloadExcel}
                  style={{
                    background: '#005537',
                    borderColor: '#005537',
                    fontWeight: 700,
                    height: 44,
                    boxShadow: '0 4px 12px rgba(0, 85, 55, 0.25)',
                  }}
                >
                  Tải File Excel (.xlsx)
                </Button>

                <Button
                  size="large"
                  icon={<CopyOutlined />}
                  onClick={handleCopyClipboard}
                  style={{ fontWeight: 600, height: 44 }}
                >
                  Sao Chép Bảng Cho Excel
                </Button>
              </div>
            </div>
          </div>

          {/* CẦN LƯU Ý / ALERTS */}
          <div className="gnt-sechead">
            <span className="eyebrow">Phân tích rủi ro & Lời khuyên</span>
            <h2>Chương trình này có gì cần đặc biệt cân nhắc?</h2>
          </div>

          <div className="gnt-alerts">
            {alerts.map((al, idx) => (
              <div key={idx} className={`gnt-alert a-${al.type}`}>
                <span className="ic">{al.icon}</span>
                <div>
                  <h3>{al.title}</h3>
                  <p>{al.desc}</p>
                </div>
              </div>
            ))}
          </div>
            </>
          )}

          {/* SO SÁNH ĐA CHƯƠNG TRÌNH */}
          <div className="gnt-sechead">
            <span className="eyebrow">Công cụ đối chiếu nhà cung cấp</span>
            <h2>Đặt các chương trình cạnh nhau để so sánh</h2>
            <p>
              Lưu chương trình đang xem rồi nạp chương trình khác vào để so sánh mức giảm giá thực tế. Hệ thống tự động xác định chương trình nào <strong>“Rẻ thật nhất”</strong>. Lưu tối đa 3 chương trình.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <Button
              type="default"
              icon={<SaveOutlined />}
              onClick={handleSaveToCompare}
              style={{ fontWeight: 600, color: '#005537', borderColor: '#005537' }}
            >
              Lưu chương trình này để so sánh
            </Button>
            {savedCompareList.length > 0 && (
              <Button type="text" danger icon={<DeleteOutlined />} onClick={handleClearCompare}>
                Xóa danh sách so sánh
              </Button>
            )}
          </div>

          {savedCompareList.length > 0 ? (
            <div className="gnt-cmp-grid">
              {savedCompareList.map((item, idx) => {
                const isWinner = idx === bestCompareIdx;
                return (
                  <div key={item.id} className={`gnt-cmpc ${isWinner ? 'win' : ''}`}>
                    {isWinner && <span className="tag-win">Rẻ Thật Nhất</span>}
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => handleRemoveCompareItem(idx)}
                      title="Bỏ chương trình này"
                    >
                      ×
                    </button>
                    <h4>{item.ten}</h4>
                    {item.ncc && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{item.ncc}</div>}
                    <div className="big">{pc(item.tongCK)}</div>
                    <ul>
                      <li>
                        <span>Ngay trên hóa đơn:</span>
                        <strong>{pc(item.ckTrenHoaDon)}</strong>
                      </li>
                      <li>
                        <span>Ngoài hóa đơn (sau đạt):</span>
                        <strong>{pc(item.ngoaiHoaDon)}</strong>
                      </li>
                      <li>
                        <span>Số lượng mã hàng:</span>
                        <strong>{item.soMa} mã</strong>
                      </li>
                      <li>
                        <span>Thời điểm lưu:</span>
                        <span style={{ color: '#94a3b8' }}>{item.thoiGian}</span>
                      </li>
                    </ul>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="gnt-empty-cmp">
              Chưa lưu chương trình nào. Bấm nút <strong>“Lưu chương trình này để so sánh”</strong> ở trên, sau đó nạp thêm chương trình của hãng khác để đối chiếu xem đơn vị nào có chiết khấu thật tốt nhất.
            </div>
          )}
        </div>
      </div>

      {/* MODAL THU LEAD TẢI EXCEL */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileExcelOutlined style={{ color: '#005537', fontSize: 20 }} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Nhận Toàn Bộ File Excel & Báo Cáo Bóc Tách</span>
          </div>
        }
        open={leadModalVisible}
        onCancel={() => setLeadModalVisible(false)}
        footer={null}
        centered
        width={480}
      >
        <p style={{ color: '#475569', fontSize: 13.5, lineHeight: 1.6, marginBottom: 18 }}>
          Vui lòng để lại thông tin nhà thuốc để hệ thống kết nối gửi trọn bộ file Excel 17 cột và hỗ trợ chuyên môn đối soát khi bạn nhập hàng từ nhà cung cấp:
        </p>

        <Form layout="vertical" onFinish={handleLeadSubmit}>
          <Form.Item
            label={<strong>Họ và tên của bạn</strong>}
            name="name"
            rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
            initialValue={user?.fullName || ''}
          >
            <Input placeholder="Dược sĩ Nguyễn Văn A" size="large" />
          </Form.Item>

          <Form.Item
            label={<strong>Số điện thoại / Zalo</strong>}
            name="phone"
            rules={[
              { required: true, message: 'Vui lòng nhập số điện thoại' },
              { pattern: /^[0-9+ ]{9,15}$/, message: 'Số điện thoại không hợp lệ' },
            ]}
            initialValue={user?.phone || ''}
          >
            <Input placeholder="0988 123 456" size="large" />
          </Form.Item>

          <Form.Item
            label={<strong>Tên Nhà thuốc / Quầy thuốc (Tùy chọn)</strong>}
            name="pharmacyName"
            initialValue={user?.pharmacyName || ''}
          >
            <Input placeholder="Nhà thuốc Minh Châu" size="large" />
          </Form.Item>

          <div style={{ marginTop: 22 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={isSubmittingLead}
              icon={<DownloadOutlined />}
              style={{
                background: '#005537',
                borderColor: '#005537',
                fontWeight: 700,
                height: 44,
              }}
            >
              Tải File Excel Ngay
            </Button>
          </div>
        </Form>
      </Modal>

      {/* MODAL TINH CHỈNH SỐ LIỆU THỦ CÔNG */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EditOutlined style={{ color: '#0d7a6f', fontSize: 20 }} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Tinh Chỉnh Thông Số Chương Trình</span>
          </div>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        centered
        width={560}
      >
        <Form form={editForm} layout="vertical" onFinish={handleSaveQuickEdit}>
          <Form.Item label={<strong>Tên chương trình</strong>} name="tenCT">
            <Input placeholder="Ví dụ: Bảng giá tháng 9..." size="large" />
          </Form.Item>

          <Form.Item label={<strong>Nhà cung cấp / Hãng dược</strong>} name="ncc">
            <Input placeholder="Công ty Dược..." size="large" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={<strong>Mức hãng công bố (%)</strong>} name="uuDaiCongBo">
                <InputNumber min={0} max={100} style={{ width: '100%' }} size="large" addonAfter="%" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<strong>CK thẳng trên hóa đơn (%)</strong>} name="ckDon">
                <InputNumber min={0} max={99} style={{ width: '100%' }} size="large" addonAfter="%" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={<strong>Thưởng doanh số tháng (%)</strong>} name="ckThang">
                <InputNumber min={0} max={99} style={{ width: '100%' }} size="large" addonAfter="%" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<strong>Thưởng doanh số quý (%)</strong>} name="ckQuy">
                <InputNumber min={0} max={99} style={{ width: '100%' }} size="large" addonAfter="%" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
            <Button onClick={() => setEditModalVisible(false)}>Hủy</Button>
            <Button type="primary" htmlType="submit" style={{ background: '#005537', borderColor: '#005537', fontWeight: 600 }}>
              Cập Nhật & Tính Lại
            </Button>
          </div>
        </Form>
      </Modal>

      {/* MODAL BÁO NỘI DUNG KHÔNG PHẢI CTKM HỢP LỆ */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: '#dc2626', fontSize: 20 }} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Không thể phân tích nội dung này</span>
          </div>
        }
        open={invalidModalVisible}
        onCancel={() => setInvalidModalVisible(false)}
        centered
        width={480}
        footer={[
          <Button key="ok" type="primary" onClick={() => setInvalidModalVisible(false)} style={{ background: '#005537', borderColor: '#005537', fontWeight: 600 }}>
            Đã hiểu, tôi sẽ tải lại file khác
          </Button>,
        ]}
      >
        <p style={{ color: '#475569', fontSize: 13.5, lineHeight: 1.6, marginBottom: 0 }}>
          {invalidReason ||
            'Nội dung tải lên không phải chương trình khuyến mãi (CTKM) của nhà cung cấp, hoặc hệ thống không đọc được thông tin sản phẩm/chiết khấu nào.'}
          {' '}Vui lòng kiểm tra lại và tải đúng file/ảnh chương trình khuyến mãi (tờ rơi, bảng giá, tin nhắn của trình dược viên...), sau đó bấm "Phân Tích Chương Trình" lại.
        </p>
      </Modal>
    </div>
  );
}
